import { IconCalendar, IconClock, IconHourglass } from '@/theme/icons';
import { formatStringToDate, utcToLocalDate } from '@/utils/calendar';
import { getDateFnsLocale } from '@/utils/dateLocale';
import { convertToMinutes } from '@/utils/events';
import { capitalizeFirstLetter, humanDuration } from '@/utils/index';
import rem from '@/utils/rem';
import { Flex, Icon, Tooltip } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

function isScheduled(scheduledDate: string | null, scheduledTime: string | null) {
  return scheduledDate !== null && scheduledTime !== null;
}

type ClockProps = {
  iconColor?: string;
  scheduledDate: string | null;
  scheduledTime: string | null;
  timeZone?: string;
  gmtOffset?: string;
  duration: string;
};

function Clock({ iconColor = 'white.500', scheduledDate, scheduledTime, duration, timeZone, gmtOffset }: ClockProps) {
  const { t, i18n } = useTranslation('ActionRow');

  // Get the appropriate locale for date-fns based on current language
  const dateLocale = getDateFnsLocale(i18n.language);

  if (isScheduled(scheduledDate, scheduledTime) && scheduledDate) {
    const formattedDate = capitalizeFirstLetter(
      format(utcToLocalDate(scheduledDate, scheduledTime, timeZone, gmtOffset), "MMMM dd',' yyyy '/' h:mma", {
        locale: dateLocale,
      }),
    );
    const durationText = humanDuration(convertToMinutes(duration) * 60);
    const label = t('tooltips.scheduled', { date: formattedDate, duration: durationText });

    return (
      <Tooltip aria-label={label} label={label} placement="top">
        <Flex>
          <Icon as={IconClock} boxSize={rem(14)} color={iconColor} />
        </Flex>
      </Tooltip>
    );
  }

  if (scheduledDate !== null) {
    const formattedDate = capitalizeFirstLetter(
      format(formatStringToDate(scheduledDate), "MMMM dd',' yyyy", { locale: dateLocale }),
    );
    const durationText = humanDuration(convertToMinutes(duration) * 60);
    const label = t('tooltips.planned', { date: formattedDate, duration: durationText });

    return (
      <Tooltip aria-label={label} label={label} placement="top">
        <Flex>
          <Icon as={IconCalendar} boxSize={rem(14)} color={iconColor} />
        </Flex>
      </Tooltip>
    );
  }

  const durationText = humanDuration(convertToMinutes(duration) * 60);
  const label = t('tooltips.duration', { duration: durationText });

  return (
    <Tooltip aria-label={label} label={label} placement="top">
      <Flex>
        <Icon as={IconHourglass} boxSize={rem(14)} color={iconColor} />
      </Flex>
    </Tooltip>
  );
}

export default Clock;
